import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageSquare, Image, User, ChevronRight, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";



const SERVICE_LABELS = {
  cargo_delivery: "Cargo", courier: "Courier", towing: "Towing",
  movers: "Movers", laborer: "Laborer", roadside_assistance: "Roadside",
  nanny: "Nanny", cleaning: "Cleaning", chef: "Chef",
};

const InboxPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const res = await axios.get(`${API}/inbox/conversations`, { withCredentials: true });
        setConversations(res.data.conversations || []);
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to load conversations"));
      }
      setLoading(false);
    };
    fetchConversations();
  }, []);

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (diff < 604800000) return d.toLocaleDateString([], { weekday: "short" });
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="inbox-page">
      <Navbar />
      <div className="pt-20 pb-20 max-w-3xl mx-auto">
        <div className="px-4 py-4">
          <h1 className="text-xl font-bold text-emerald-950" data-testid="inbox-title">Inbox</h1>
          <p className="text-sm text-stone-500 mt-0.5">Your conversations</p>
        </div>

        {loading ? (
          <div className="space-y-3 px-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-stone-100 p-4 animate-pulse">
                <div className="flex gap-3">
                  <div className="w-12 h-12 bg-stone-200 rounded-full shrink-0" />
                  <div className="flex-1">
                    <div className="h-4 bg-stone-200 rounded w-1/3 mb-2" />
                    <div className="h-3 bg-stone-100 rounded w-2/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-16 px-4">
            <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 font-medium">No conversations yet</p>
            <p className="text-sm text-stone-400 mt-1">Messages from active jobs will appear here</p>
          </div>
        ) : (
          <div className="px-4 space-y-2" data-testid="conversations-list">
            {conversations.map((convo, idx) => (
              <motion.div key={convo.request_id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
                <Link
                  to={`/chat/${convo.request_id}`}
                  className="flex items-center gap-3 bg-white rounded-2xl border border-stone-100 p-3.5 hover:border-emerald-200 transition-colors"
                  data-testid={`conversation-${convo.request_id}`}
                >
                  {/* Avatar */}
                  <div className="relative shrink-0">
                    {convo.partner_picture ? (
                      <img src={convo.partner_picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-emerald-600" />
                      </div>
                    )}
                    {convo.chat_active && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className={`text-sm truncate ${convo.unread_count > 0 ? "font-bold text-emerald-950" : "font-medium text-stone-800"}`}>
                        {convo.partner_name}
                      </h3>
                      <span className="text-[10px] text-stone-400 shrink-0">{formatTime(convo.last_message_time)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <p className={`text-xs truncate ${convo.unread_count > 0 ? "text-stone-700 font-medium" : "text-stone-500"}`}>
                        {convo.last_message_image && !convo.last_message ? (
                          <span className="inline-flex items-center gap-1"><Image className="w-3 h-3" /> Photo</span>
                        ) : convo.last_message ? (
                          convo.last_message
                        ) : (
                          <span className="text-stone-400 italic">No messages yet</span>
                        )}
                      </p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {convo.unread_count > 0 && (
                          <span className="w-5 h-5 bg-emerald-600 text-white rounded-full text-[10px] flex items-center justify-center font-bold" data-testid={`unread-${convo.request_id}`}>
                            {convo.unread_count}
                          </span>
                        )}
                        <ChevronRight className="w-4 h-4 text-stone-300" />
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0 capitalize">{SERVICE_LABELS[convo.service_type] || convo.service_type}</Badge>
                      <Badge variant="outline" className={`text-[9px] px-1.5 py-0 capitalize ${convo.chat_active ? "border-green-200 text-green-700" : "border-stone-200 text-stone-500"}`}>
                        {convo.status}
                      </Badge>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxPage;
